package com.example.mystybag.appCore.views.pages

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.BasicAlertDialog
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Surface
import androidx.compose.material3.TextButton
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.layout.ContentScale
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.widthIn
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.zIndex
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.appCore.logic.pages.BagViewModel
import com.example.mystybag.appCore.views.components.MenuPhotosSection
import com.example.mystybag.common.extensions.Image
import com.example.mystybag.common.extensions.scrollEdgeFade
import com.example.mystybag.common.extensions.rippleClick
import com.example.mystybag.common.ui.components.custom.ShimmerAsyncImage
import com.example.mystybag.common.ui.components.native.CircularQuantityButton
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.ui.components.native.Texts.StrikeThroughText
import com.example.mystybag.common.ui.views.UIView
import com.example.mystybag.core.ui.theme.LocalTheme.dialogCloseButtonIconSizeDp
import com.example.mystybag.core.ui.theme.LocalTheme.dialogCloseButtonPaddingDp
import com.example.mystybag.core.ui.theme.LocalTheme.dialogCloseButtonSizeDp
import com.example.mystybag.core.ui.theme.LocalTheme.p10
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import com.example.mystybag.core.ui.theme.LocalTheme.p6
import com.example.mystybag.core.ui.theme.LocalTheme.p7
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p40
import com.example.mystybag.core.ui.theme.LocalTheme.p100
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.extensions.formatAmount
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.sharedmodels.project.responseModels.merchant.ProductModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.distinctCategoryCount
import com.example.mystybag.sharedmodels.project.responseModels.merchant.groupByCategory
import com.example.mystybag.core.ui.theme.LocalTheme.p2
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p56
import dev.icerock.moko.resources.compose.painterResource

// Figma Details: Rectangle 12 = 412×251, Ellipse 6 = 92×92, Frame 72 radius 28
private const val COVER_HEIGHT_DP = 240
private const val MERCHANT_LOGO_SIZE_DP = 92
private const val BAG_CARD_RADIUS_DP = 28

@Composable
internal fun BagAdultProductsDisclosure(modifier: Modifier = Modifier) {
    val colors = AppTheme.current.colors
    Row(
        modifier = modifier,
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(p8)
    ) {
        Image(
            painter = painterResource(SRC.images.bag_age_restricted_18),
            modifier = Modifier.size(24.dp),
            colorFilter = ColorFilter.tint(colors.error)
        )
        Texts.BodyMedium(
            text = sharedString(SR.strings.bag_adult_products_disclosure),
            color = colors.error
        )
    }
}

@Composable
@OptIn(ExperimentalMaterial3Api::class)
fun BagView(viewModel: BagViewModel) {
    if (PlatformHelper.isWeb) {
        BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
            if (maxWidth >= 1200.dp) {
                BagWebView(viewModel = viewModel)
            } else {
                BagViewMobileContent(viewModel = viewModel)
            }
        }
        return
    }

    BagViewMobileContent(viewModel = viewModel)
}

@Composable
@OptIn(ExperimentalMaterial3Api::class)
private fun BagViewMobileContent(viewModel: BagViewModel) {
    val uiState = viewModel.uiState.subscribeAsState()
    var showRatingDialog by remember { mutableStateOf(false) }
    var showPossibleProductsBottomSheet by remember { mutableStateOf(false) }
    var showAllergensBottomSheet by remember { mutableStateOf(false) }
    val scrollState = rememberScrollState()
    val allergenNames = uiState.value.allergens
        .mapNotNull { it.name?.trim() }
        .filter { it.isNotEmpty() }
        .distinct()
    val merchantAllergensText = uiState.value.merchantAllergens?.trim().orEmpty()
    val hasAllergensContent = allergenNames.isNotEmpty() || merchantAllergensText.isNotEmpty()

    UIView(
        viewModel = viewModel,
        removeVerticalPadding = true,
        navigationContent = null,
        showTopBar = false
    ) {
        Column(
            modifier = Modifier
                .fillMaxSize()
        ) {
            Box(modifier = Modifier.weight(1f)) {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .verticalScroll(scrollState)
                        .scrollEdgeFade(showTop = false, showBottom = false)
                ) {
                    // ----- Cover photo: starts from very top of screen, gradient only at bottom -----
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(COVER_HEIGHT_DP.dp)
                    ) {
                        val density = LocalDensity.current
                        val coverHeightPx = with(density) { COVER_HEIGHT_DP.dp.roundToPx() }
                        val headerProgress = (scrollState.value.toFloat() / coverHeightPx.toFloat())
                            .coerceIn(0f, 1f)
                        val isAtBottom = scrollState.maxValue > 0 && scrollState.value >= scrollState.maxValue
                        val collapseProgress = if (isAtBottom) 1f else headerProgress
                        val parallaxTranslationY = scrollState.value * 0.35f * (1f - collapseProgress)
                        Box(
                            modifier = Modifier
                                .matchParentSize()
                                .clipToBounds()
                                .graphicsLayer {
                                    // Keeps the image moving with the content, but slightly slower for a native-like effect.
                                    translationY = parallaxTranslationY
                                    alpha = 1f - collapseProgress
                                }
                        ) {
                            ShimmerAsyncImage(
                                modifier = Modifier.fillMaxSize(),
                                link = uiState.value.locationCoverUrl,
                                placeholder = null,
                                useOriginalSize = false,
                                contentScale = ContentScale.Crop
                            )
                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .background(
                                        Brush.verticalGradient(
                                            colors = listOf(
                                                Color.Transparent,
                                                AppTheme.current.colors.background
                                            ),
                                            startY = 0f,
                                            endY = Float.POSITIVE_INFINITY
                                        )
                                    )
                            )
                        }
                    }

                    // ----- Merchant logo: round, centered, overlapping cover and white -----
                    val merchantImageUrl = uiState.value.merchantImageUrl
                    if (!merchantImageUrl.isNullOrBlank()) {
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .zIndex(1f)
                        ) {
                            Box(
                                modifier = Modifier
                                    .align(Alignment.TopCenter)
                                    .offset(y = (-MERCHANT_LOGO_SIZE_DP / 2).dp)
                                    .size(MERCHANT_LOGO_SIZE_DP.dp)
                                    .zIndex(1f)
                                    .clip(CircleShape)
                                    .background(AppTheme.current.colors.surface),
                                contentAlignment = Alignment.Center
                            ) {
                                ShimmerAsyncImage(
                                    modifier = Modifier,
                                    link = merchantImageUrl,
                                    placeholder = null,
                                    useOriginalSize = false,
                                    contentScale = ContentScale.Crop
                                )
                            }
                        }
                    }

                    // ----- White section: location name (title), category, rating, location block -----
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .background(AppTheme.current.colors.background)
                            .padding(horizontal = p16),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        // Location name – Headline Small Medium 24, centered (fallback to merchantTitle if locationTitle is empty)
                        val locationNameTitle = uiState.value.locationTitle.ifEmpty { uiState.value.merchantTitle }
                        // Frame 75: store name 380×32, itemSpacing 10
                        Texts.HeadlineSmall(
                            text = locationNameTitle,
                            color = AppTheme.current.colors.onSurface,
                            textAlign = androidx.compose.ui.text.style.TextAlign.Center
                        )
                        Spacer(modifier = Modifier.height(p10))

                        // Frame 2: category tag 127×30, radius 100, padding 12/7, Label Medium 12
                        if (uiState.value.businessCategoryTitle != null) {
                            CategoryTag(text = uiState.value.businessCategoryTitle!!)
                            Spacer(modifier = Modifier.height(p10))
                        }

                        // Frame 48: rating chip — only when there is at least one review
                        if (uiState.value.reviewCount > 0) {
                            Row(
                                modifier = Modifier
                                    .clip(RoundedCornerShape(p100))
                                    .background(AppTheme.current.colors.surfaceContainerHigh.copy(alpha = 0.2f))
                                    .padding(start = p2, top = p2, end = p2, bottom = p2)
                                    .rippleClick { showRatingDialog = true },
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(p4)
                            ) {
                                Box(modifier = Modifier.size(20.dp), contentAlignment = Alignment.Center) {
                                    Image(
                                        modifier = Modifier.size(12.dp),
                                        painter = painterResource(SRC.images.home_top_pick_star),
                                        colorFilter = ColorFilter.tint(AppTheme.current.colors.onSurface)
                                    )
                                }
                                Texts.TitleSmall(
                                    text = com.example.mystybag.core.expected.format("%.1f", uiState.value.averageRating),
                                    color = AppTheme.current.colors.onSurface
                                )
                                Texts.BodyMedium(
                                    text = " • ",
                                    color = AppTheme.current.colors.onSurfaceVariant
                                )
                                Texts.BodyMedium(
                                    text = sharedString(SR.strings.reviews_count, uiState.value.reviewCount),
                                    color = AppTheme.current.colors.onPrimaryContainer
                                )
                            }
                        }
                        Spacer(modifier = Modifier.height(p20))

                        // Frame 71: location/hours/collection, itemSpacing 8; each row itemSpacing 6, icon 14×14; центрирани по хоризонтала
                        Column(
                            modifier = Modifier.fillMaxWidth(),
                            verticalArrangement = Arrangement.spacedBy(p8),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            val hasLocationAddress = uiState.value.locationAddress.isNotBlank()
                            Row(
                                modifier = if (hasLocationAddress) {
                                    Modifier.rippleClick { viewModel.onLocationClick() }
                                } else {
                                    Modifier
                                },
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(p6)
                            ) {
                                Image(
                                    modifier = Modifier.size(14.dp),
                                    painter = painterResource(SRC.images.bag_location),
                                    colorFilter = ColorFilter.tint(AppTheme.current.colors.onSurfaceVariant)
                                )
                                Texts.BodyMedium(
                                    text = buildAddressLine(uiState.value.locationAddress, uiState.value.distance),
                                    color = if (hasLocationAddress) {
                                        AppTheme.current.colors.onPrimaryContainer
                                    } else {
                                        AppTheme.current.colors.onSurfaceVariant
                                    },
                                    textDecoration = if (hasLocationAddress) TextDecoration.Underline else null
                                )
                            }
                            if (!uiState.value.openingHours.isNullOrEmpty()) {
                                Row(
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.spacedBy(p6)
                                ) {
                                    Image(
                                        modifier = Modifier.size(14.dp),
                                        painter = painterResource(SRC.images.bag_location),
                                        colorFilter = ColorFilter.tint(AppTheme.current.colors.onSurfaceVariant)
                                    )
                                    Texts.BodyMedium(
                                        text = sharedString(SR.strings.open_until, uiState.value.openingHours!!),
                                        color = AppTheme.current.colors.onSurfaceVariant
                                    )
                                }
                            }
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(p6)
                            ) {
                                Image(
                                    modifier = Modifier.size(14.dp),
                                    painter = painterResource(SRC.images.bag_collect),
                                    colorFilter = ColorFilter.tint(AppTheme.current.colors.onSurfaceVariant)
                                )
                                Texts.BodyMedium(
                                    text = uiState.value.collectWindowLabel,
                                    color = AppTheme.current.colors.onSurfaceVariant
                                )
                            }
                        }
                        Spacer(modifier = Modifier.height(p24))

                        // Figma Frame 79: 380×176, itemSpacing 16; Frame 72: radius 28, padding 20, itemSpacing 12
                        BagDetailsCard(
                            bagTitle = uiState.value.bagTitle ?: sharedString(SR.strings.surprise_bag),
                            bagsAvailable = uiState.value.bagsAvailable,
                            hasProducts = uiState.value.products.isNotEmpty(),
                            priceNormal = uiState.value.priceNormal,
                            priceWithDiscount = uiState.value.priceWithDiscount,
                            currency = uiState.value.currency,
                            hasAllergensContent = hasAllergensContent,
                            containsProductsForPersonsOver18 = uiState.value.containsProductsForPersonsOver18,
                            onViewPossibleProductsClick = { showPossibleProductsBottomSheet = true },
                            onAllergensClick = { showAllergensBottomSheet = true }
                        )

                        // Selling fast alert
                        if (uiState.value.isSellingFast) {
                            SellingFastAlert(modifier = Modifier.padding(top = p8))
                            Spacer(modifier = Modifier.height(p16))
                        }

                        if (uiState.value.menuPhotoUrls.isNotEmpty()) {
                            MenuPhotosSection(
                                menuPhotoUrls = uiState.value.menuPhotoUrls,
                                onMenuPhotoClick = viewModel::onMenuPhotoClick,
                                modifier = Modifier.fillMaxWidth().padding(top = p32)
                            )
                        }
                        Spacer(modifier = Modifier.height(p24))
                    }
                }

                // Static top action buttons (do not scroll with content)
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .align(Alignment.TopCenter)
                        .statusBarsPadding()
                        .padding(horizontal = p16)
                        .height(48.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Box(
                        modifier = Modifier
                            .size(48.dp)
                            .clip(CircleShape)
                            .background(Color.White)
                            .rippleClick { viewModel.onBackClick() },
                        contentAlignment = Alignment.Center
                    ) {
                        Image(
                            modifier = Modifier.size(18.dp),
                            painter = painterResource(SRC.images.arrow_back),
                            alignment = Alignment.Center,
                            contentScale = ContentScale.Fit
                        )
                    }
                    Box(
                        modifier = Modifier
                            .size(48.dp)
                            .clip(CircleShape)
                            .background(Color.White)
                            .rippleClick { viewModel.onFavoriteClick() },
                        contentAlignment = Alignment.Center
                    ) {
                        Image(
                            modifier = Modifier.size(22.dp),
                            painter = painterResource(
                                if (uiState.value.isFavorite) SRC.images.nav_favorites else SRC.images.heart_favorite
                            ),
                            colorFilter = if (uiState.value.isFavorite) ColorFilter.tint(AppTheme.current.colors.primary) else null,
                            alignment = Alignment.Center,
                            contentScale = ContentScale.Fit
                        )
                    }
                }
            }

            // Figma Button: 380×56, cornerRadius 100, padding 10; Frame 81 itemSpacing 24 above button
            val canReserve = uiState.value.bagsAvailable > 0
            DefaultButton(
                caption = if (canReserve) sharedString(SR.strings.reserve) else sharedString(SR.strings.sold_out),
                callback = { viewModel.onReserveClick() },
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = p16, vertical = p8),
                enabled = canReserve,
            )

            // ----- Rating details dialog (when tapping on rating row) -----
            if (showRatingDialog) {
                com.example.mystybag.appCore.views.components.RatingDetailsDialog(
                    averageRating = uiState.value.averageRating,
                    reviewCount = uiState.value.reviewCount,
                    businessCategoryId = uiState.value.businessCategoryId,
                    ratingCategory1 = uiState.value.ratingCategory1,
                    ratingCategory2 = uiState.value.ratingCategory2,
                    ratingCategory3 = uiState.value.ratingCategory3,
                    ratingCategory4 = uiState.value.ratingCategory4,
                    onDismiss = { showRatingDialog = false }
                )
            }

            if (showPossibleProductsBottomSheet) {
                val possibleProducts = uiState.value.products
                val showGroups = possibleProducts.distinctCategoryCount() > 1
                ModalBottomSheet(
                    onDismissRequest = { showPossibleProductsBottomSheet = false },
                    sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true),
                    containerColor = AppTheme.current.colors.surface
                ) {
                    val sheetScroll = rememberScrollState()
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .verticalScroll(sheetScroll)
                            .padding(horizontal = p16, vertical = p16),
                        verticalArrangement = Arrangement.spacedBy(p12)
                    ) {
                        if (possibleProducts.isNotEmpty()) {
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.SpaceBetween
                            ) {
                                Texts.LabelLarge(
                                    text = sharedString(SR.strings.product_name),
                                    color = AppTheme.current.colors.onSurface,
                                    modifier = Modifier.weight(1f)
                                )
                                Texts.LabelLarge(
                                    text = sharedString(SR.strings.grams),
                                    color = AppTheme.current.colors.onSurface,
                                    modifier = Modifier.padding(start = p12)
                                )
                            }
                            if (showGroups) {
                                possibleProducts.groupByCategory(uiState.value.productCategories).forEach { group ->
                                    val groupName = group.name?.trim().orEmpty()
                                    if (groupName.isNotEmpty()) {
                                        Texts.TitleSmall(
                                            text = groupName,
                                            color = AppTheme.current.colors.onSurface,
                                            modifier = Modifier
                                                .fillMaxWidth()
                                                .padding(top = p8)
                                        )
                                    }
                                    group.products.forEach { product ->
                                        PossibleProductRow(product = product)
                                    }
                                }
                            } else {
                                possibleProducts.forEach { product ->
                                    PossibleProductRow(product = product)
                                }
                            }
                        }
                        Spacer(modifier = Modifier.height(p32))
                    }
                }
            }
            if (showAllergensBottomSheet) {
                ModalBottomSheet(
                    onDismissRequest = { showAllergensBottomSheet = false },
                    sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true),
                    containerColor = AppTheme.current.colors.surface
                ) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = p16, vertical = p16),
                        verticalArrangement = Arrangement.spacedBy(p12)
                    ) {
                        Texts.TitleMedium(text = sharedString(SR.strings.allergens))
                        if (allergenNames.isNotEmpty()) {
                            allergenNames.forEach { allergenName ->
                                Texts.BodyMedium(
                                    text = "• $allergenName",
                                    color = AppTheme.current.colors.onSurfaceVariant
                                )
                            }
                        } else if (merchantAllergensText.isNotEmpty()) {
                            Texts.BodyMedium(
                                text = merchantAllergensText,
                                color = AppTheme.current.colors.onSurfaceVariant
                            )
                        } else {
                            Texts.BodyMedium(
                                text = "—",
                                color = AppTheme.current.colors.onSurfaceVariant
                            )
                        }
                        Spacer(modifier = Modifier.height(p32))
                    }
                }
            }

            // ----- Reserve a bag dialog (replaces bottom sheet) -----
            if (uiState.value.showReserveDialog) {
                val reserveQty = uiState.value.reserveQuantity
                val moreAvail = uiState.value.bagsAvailable
                ReserveBagDialog(
                    quantity = reserveQty,
                    moreAvailable = moreAvail,
                    isLoading = uiState.value.reserveDialogLoading || uiState.value.checkoutInProgress,
                    errorMessage = uiState.value.reserveDialogErrorMessage,
                    onDismiss = { viewModel.onReserveDialogClose() },
                    onDecrease = { viewModel.onDecreaseReserveQuantity() },
                    onIncrease = { viewModel.onIncreaseReserveQuantity() },
                    onConfirm = { viewModel.confirmReservation() }
                )
            }

            // Warn the user before reserving a bag whose pickup window is currently open.
            if (uiState.value.showPickupNowConfirm) {
                PickupNowConfirmDialog(
                    hasLocationPermission = uiState.value.pickupNowHasLocationPermission,
                    walkMinutes = uiState.value.pickupNowWalkMinutes,
                    driveMinutes = uiState.value.pickupNowDriveMinutes,
                    onDismiss = { viewModel.onPickupNowConfirmCancel() },
                    onContinue = { viewModel.onPickupNowConfirmContinue() }
                )
            }
        }
    }
}

private fun buildAddressLine(address: String, distance: String): String {
    return when {
        address.isNotEmpty() && distance.isNotEmpty() -> "$address • $distance"
        address.isNotEmpty() -> address
        distance.isNotEmpty() -> distance
        else -> ""
    }
}

// Figma Frame 72: 380×176, cornerRadius 28, padding 20, itemSpacing 12; Frame 56 + Frame 12 (rating badge)
@Composable
private fun BagDetailsCard(
    bagTitle: String,
    bagsAvailable: Int,
    hasProducts: Boolean,
    priceNormal: Double,
    priceWithDiscount: Double,
    currency: String,
    hasAllergensContent: Boolean,
    containsProductsForPersonsOver18: Boolean,
    onViewPossibleProductsClick: () -> Unit,
    onAllergensClick: () -> Unit
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(BAG_CARD_RADIUS_DP.dp))
            .background(AppTheme.current.colors.surfaceContainerLow)
            .padding(p16),
        horizontalArrangement = Arrangement.spacedBy(p12)
    ) {
        Column(
            modifier = Modifier.weight(1f),
            verticalArrangement = Arrangement.spacedBy(p12)
        ) {
            // Frame 80: bag name + availability, itemSpacing 2
            Column(verticalArrangement = Arrangement.spacedBy(p2)) {
                Texts.TitleMedium(
                    text = bagTitle,
                    color = AppTheme.current.colors.onSurface
                )
                Texts.BodyMedium(
                    text = sharedString(SR.strings.available_count, bagsAvailable),
                    color = AppTheme.current.colors.onSurfaceVariant
                )
            }
            // Frame 48: prices, itemSpacing 8
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(p8)
            ) {
                StrikeThroughText(
                    text = formatPrice(priceNormal, currency),
                    style = AppTheme.current.typography.bodyLarge,
                    color = AppTheme.current.colors.onSurfaceVariant
                )
                Texts.HeadlineSmall(
                    text = formatPrice(priceWithDiscount, currency),
                    color = AppTheme.current.colors.onSurface
                )
            }
            Column(verticalArrangement = Arrangement.spacedBy(p6)) {
                if (containsProductsForPersonsOver18) {
                    BagAdultProductsDisclosure()
                }
                if (hasProducts) {
                    TextButton(
                        onClick = onViewPossibleProductsClick,
                        contentPadding = androidx.compose.foundation.layout.PaddingValues(0.dp)
                    ) {
                        Texts.LabelLarge(
                            text = sharedString(SR.strings.view_possible_products),
                            color = AppTheme.current.colors.primary
                        )
                    }
                }
                if (hasAllergensContent) {
                    TextButton(
                        onClick = onAllergensClick,
                        contentPadding = androidx.compose.foundation.layout.PaddingValues(0.dp)
                    ) {
                        Texts.LabelLarge(
                            text = sharedString(SR.strings.allergens),
                            color = AppTheme.current.colors.primary
                        )
                    }
                } else {
                    Texts.BodyMedium(
                        text = sharedString(SR.strings.product_contains_no_allergens),
                        color = AppTheme.current.colors.onSurfaceVariant
                    )
                }
            }
        }
    }
}

// Figma Frame 2: 127×30, cornerRadius 100, padding 12/7, Label Medium 12
@Composable
private fun PossibleProductRow(product: ProductModel) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top
    ) {
        Texts.BodyMedium(
            text = product.name.ifEmpty { "—" },
            color = AppTheme.current.colors.onSurface,
            modifier = Modifier.weight(1f)
        )
        Texts.BodyMedium(
            text = if (product.grams > 0) "${product.grams} g" else "—",
            color = AppTheme.current.colors.onSurfaceVariant,
            modifier = Modifier.padding(start = p12)
        )
    }
}

@Composable
private fun CategoryTag(text: String) {
    Box(
        modifier = Modifier
            .clip(RoundedCornerShape(p100))
            .background(AppTheme.current.colors.surfaceVariant)
            .padding(horizontal = p12, vertical = p7)
    ) {
        Texts.LabelMedium(
            text = text,
            color = AppTheme.current.colors.onSurfaceVariant
        )
    }
}

@Composable
private fun SellingFastAlert(modifier: Modifier = Modifier) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(p12))
            .background(AppTheme.current.colors.primary.copy(alpha = 0.1f))
            .padding(p16),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(p12)
    ) {
        Texts.LabelLarge(text = "🚀")
        Column {
            Texts.LabelLarge(
                text = sharedString(SR.strings.item_selling_fast),
                color = AppTheme.current.colors.onSurface
            )
            Texts.BodySmall(
                text = sharedString(SR.strings.surprise_bag_high_demand),
                color = AppTheme.current.colors.onSurfaceVariant
            )
        }
    }
}

// Figma Reserve a bag dialog: 380×336, cornerRadius 40, content width 316, close button 34×34 in cut-out
private val ReserveBagDialogWidthDp = 380.dp
private val ReserveBagDialogHeightDp = 336.dp
private val ReserveBagDialogRadiusDp = 40.dp
private val ReserveBagDialogContentWidthDp = 316.dp
private val ReserveBagDialogHorizontalPaddingDp = (ReserveBagDialogWidthDp - ReserveBagDialogContentWidthDp) / 2
private val ReserveBagQuantitySpacingDp = 40.dp
private val ReserveBagConfirmCornerRadiusDp = 100.dp

private val ReserveBagDialogCornerRadiusDp = 40.dp

@Composable
private fun ReserveBagDialog(
    quantity: Int,
    moreAvailable: Int,
    isLoading: Boolean,
    errorMessage: String,
    onDismiss: () -> Unit,
    onDecrease: () -> Unit,
    onIncrease: () -> Unit,
    onConfirm: () -> Unit
) {
    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .windowInsetsPadding(WindowInsets.safeDrawing),
            contentAlignment = Alignment.BottomCenter
        ) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .widthIn(max = ReserveBagDialogWidthDp)
                    .height(ReserveBagDialogHeightDp)
                    .padding(p16)
            ) {
                Surface(
                    modifier = Modifier.fillMaxSize(),
                    shape = RoundedCornerShape(ReserveBagDialogCornerRadiusDp),
                    color = AppTheme.current.colors.surface
                ) {
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .padding(horizontal = ReserveBagDialogHorizontalPaddingDp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        // Top spacer for overlap with close button; title area
                        Spacer(modifier = Modifier.height(p40))
                        // Title "Reserve a bag" – 316×28 (HeadlineMedium)
                        Texts.HeadlineMedium(
                            modifier = Modifier.fillMaxWidth(),
                            text = sharedString(SR.strings.reserve_a_bag),
                            color = AppTheme.current.colors.onSurface,
                            textAlign = androidx.compose.ui.text.style.TextAlign.Center
                        )
                        Spacer(modifier = Modifier.height(p8))
                        // "How many bags would you like to reserve?" – 316×20
                        Texts.BodyMedium(
                            modifier = Modifier.fillMaxWidth(),
                            text = sharedString(SR.strings.how_many_bags_would_you_like_to_reserve),
                            color = AppTheme.current.colors.onSurface,
                            textAlign = androidx.compose.ui.text.style.TextAlign.Center
                        )
                        // Frame 83: 316×92, itemSpacing 16
                        Spacer(modifier = Modifier.height(p16))
                        // Frame 82: minus | 40dp | quantity | 40dp | plus (56×56 circles)
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(ReserveBagQuantitySpacingDp)
                        ) {
                            CircularQuantityButton(
                                symbol = "−",
                                backgroundColor = AppTheme.current.colors.surfaceContainerLow,
                                contentColor = AppTheme.current.colors.onSurface,
                                enabled = quantity > 1,
                                onClick = onDecrease
                            )
                            Texts.HeadlineSmall(
                                text = quantity.toString(),
                                color = AppTheme.current.colors.onSurface
                            )
                            CircularQuantityButton(
                                symbol = "+",
                                backgroundColor = AppTheme.current.colors.primary,
                                contentColor = AppTheme.current.colors.onPrimary,
                                enabled = moreAvailable > 0 && quantity < moreAvailable,
                                onClick = onIncrease
                            )
                        }
                        // Един ред: реална наличност от API; при 1 — "последна торба"
                        Spacer(modifier = Modifier.height(p16))
                        Texts.LabelLarge(
                            modifier = Modifier.fillMaxWidth(),
                            text = if (moreAvailable == 1) sharedString(SR.strings.last_bag) else sharedString(SR.strings.more_available, moreAvailable),
                            color = AppTheme.current.colors.onSurfaceVariant,
                            textAlign = androidx.compose.ui.text.style.TextAlign.Center
                        )
                        Spacer(modifier = Modifier.height(p16))
                        if (errorMessage.isNotBlank()) {
                            Texts.BodySmall(
                                modifier = Modifier.fillMaxWidth(),
                                text = errorMessage,
                                color = AppTheme.current.colors.error,
                                textAlign = androidx.compose.ui.text.style.TextAlign.Center
                            )
                            Spacer(modifier = Modifier.height(p12))
                        }
                        // Confirm: keep button visible while loading, disable interaction
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(p56),
                            contentAlignment = Alignment.Center
                        ) {
                            DefaultButton(
                                caption = if (isLoading) " " else sharedString(SR.strings.confirm),
                                callback = onConfirm,
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(p56)
                                    .clip(RoundedCornerShape(ReserveBagConfirmCornerRadiusDp)),
                                type = DefaultButtonType.PRIMARY,
                                enabled = !isLoading
                            )
                            if (isLoading) {
                                androidx.compose.material3.CircularProgressIndicator(
                                    modifier = Modifier.size(p16),
                                    strokeWidth = p2,
                                    color = AppTheme.current.colors.onPrimary
                                )
                            }
                        }
                    }
                }
                // X бутон – общи константи от темата (същият размер и позиция като в диалога за локация)
                Box(
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .padding(dialogCloseButtonPaddingDp)
                        .size(dialogCloseButtonSizeDp)
                        .clip(CircleShape)
                        .background(AppTheme.current.colors.surface)
                        .rippleClick(action = onDismiss),
                    contentAlignment = Alignment.Center
                ) {
                    Image(
                        modifier = Modifier.size(dialogCloseButtonIconSizeDp),
                        painter = painterResource(SRC.images.close_button),
                        contentScale = ContentScale.Fit,
                        colorFilter = ColorFilter.tint(AppTheme.current.colors.onSurface)
                    )
                }
            }
        }
    }
}

private fun formatPrice(price: Double, currency: String): String {
    val formatted = price.toString().formatAmount()
    return "$formatted $currency"
}

// Confirmation shown before reserving a bag whose pickup window is currently open.
private val PickupNowDialogWidthDp = 360.dp
private val PickupNowDialogCornerRadiusDp = 28.dp
private val PickupNowDialogPaddingHorizontalDp = 24.dp
private val PickupNowDialogPaddingVerticalDp = 28.dp
private val PickupNowDialogContentSpacingDp = 24.dp
private val PickupNowDialogTextBlockSpacingDp = 12.dp
private val PickupNowDialogButtonsSpacingDp = 8.dp

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun PickupNowConfirmDialog(
    hasLocationPermission: Boolean,
    walkMinutes: Int?,
    driveMinutes: Int?,
    onDismiss: () -> Unit,
    onContinue: () -> Unit
) {
    BasicAlertDialog(
        onDismissRequest = onDismiss,
        modifier = Modifier.padding(p4),
        properties = DialogProperties(dismissOnBackPress = true, dismissOnClickOutside = true),
        content = {
            Column(
                modifier = Modifier
                    .widthIn(max = PickupNowDialogWidthDp)
                    .clip(RoundedCornerShape(PickupNowDialogCornerRadiusDp))
                    .background(AppTheme.current.colors.surface)
                    .padding(
                        start = PickupNowDialogPaddingHorizontalDp,
                        top = PickupNowDialogPaddingVerticalDp,
                        end = PickupNowDialogPaddingHorizontalDp,
                        bottom = PickupNowDialogPaddingVerticalDp
                    )
            ) {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(PickupNowDialogTextBlockSpacingDp)
                ) {
                    Texts.HeadlineMedium(text = sharedString(SR.strings.pickup_window_open_title))
                    Texts.LabelLarge(
                        text = sharedString(SR.strings.pickup_window_open_message),
                        color = AppTheme.current.colors.onSurfaceVariant
                    )
                    if (hasLocationPermission) {
                        if (walkMinutes != null || driveMinutes != null) {
                            Column(verticalArrangement = Arrangement.spacedBy(p4)) {
                                if (walkMinutes != null) {
                                    Texts.LabelLarge(
                                        text = sharedString(SR.strings.pickup_walk_time, walkMinutes),
                                        color = AppTheme.current.colors.onSurface
                                    )
                                }
                                if (driveMinutes != null) {
                                    Texts.LabelLarge(
                                        text = sharedString(SR.strings.pickup_drive_time, driveMinutes),
                                        color = AppTheme.current.colors.onSurface
                                    )
                                }
                            }
                        }
                    } else {
                        Texts.BodySmall(
                            text = sharedString(SR.strings.enable_location_for_travel_time),
                            color = AppTheme.current.colors.onSurfaceVariant
                        )
                    }
                }
                Spacer(modifier = Modifier.height(PickupNowDialogContentSpacingDp))
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(PickupNowDialogButtonsSpacingDp)
                ) {
                    DefaultButton(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(p56),
                        caption = sharedString(SR.strings.continue_camel_case),
                        type = DefaultButtonType.PRIMARY,
                        callback = onContinue
                    )
                    DefaultButton(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(p56),
                        caption = sharedString(SR.strings.cancel),
                        type = DefaultButtonType.SECONDARY_NEUTRAL,
                        callback = onDismiss
                    )
                }
            }
        }
    )
}
